import React, { useState, useEffect } from "react";
import styled from "styled-components";
import NewSpellingGame from "../../components/NewSpellingGame";
import Keyboard from "../../components/Keyboard";
import useSpeech from "../../hooks/useSpeech";
import { Scale } from 'react-scaling';
import Papa from "papaparse";

let filteredQuestions = [];

export default function Game({ ...props }) {
    const [loading, setLoading] = useState(true);
    const [correctQuestions, setCorrectQuestions] = useState([]);
    const [incorrectQuestions, setIncorrectQuestions] = useState([]);
    const [qanswers, setQanswers] = useState([]); // NEW: Stores absolute chronological submissions
    const [score, setScore] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState(undefined);
    const { sayWord } = useSpeech();

    // Logic updated to match your original script's require pattern
    const playAudioWithFallback = (question) => {
        if (!question) return;

        // Use audio_ref from CSV, falling back to the word itself
        const ref = (question.audio_ref || question.word).toLowerCase();
        console.log("Attempting to play from public/audio: " + ref);

        // Create the path relative to the public root
        const audioPath = "/audio/" + ref + ".mp3";
        const audio = new Audio(audioPath);

        audio.play()
            .then(() => {
                console.log("Playback started successfully");
            })
            .catch(err => {
                // This catches BOTH missing files (404s) and browser autoplay blocks
                console.warn("Public audio file missing or blocked, falling back to TTS:", err);

                const msg = new SpeechSynthesisUtterance();
                msg.text = question.word;
                window.speechSynthesis.speak(msg);
            });
    };

    useEffect(() => {
        const fetchCSV = async () => {
            try {
                const response = await fetch("/questions/new_spelling_assessments.csv");
                const csvText = await response.text();
                Papa.parse(csvText, {
                    header: false,
                    skipEmptyLines: true,
                    complete: (results) => {
                        const id = props.assessment_id || "";
                        const parts = id.split('_');
                        const sanitize = (str) => (str || "").toString().replace(/\s/g, "").toLowerCase();

                        const targetName = sanitize(parts[0]);
                        const targetYear = parts.length > 1 ? sanitize(parts[1]) : "";
                        const targetPaper = parts.length > 2 ? sanitize(parts[2]) : "";

                        const matches = results.data.slice(1).filter(row => {
                            if (row.length < 6) return false;
                            return sanitize(row[1]) === targetName &&
                                (targetYear === "" || sanitize(row[2]) === targetYear) &&
                                (targetPaper === "" || sanitize(row[3]) === targetPaper);
                        }).map(row => ({
                            question_no: parseInt(row[4]) || 0,
                            word: row[5],
                            sentence: row[6],
                            audio_ref: row[7],
                            topic_id: row[8] // Added to grab Topic IDs from column 9
                        })).sort((a, b) => a.question_no - b.question_no);

                        filteredQuestions = matches;
                        if (filteredQuestions.length > 0) setCurrentQuestion(filteredQuestions[0]);
                        setLoading(false);
                    }
                });
            } catch (e) { setLoading(false); }
        };
        fetchCSV();
    }, [props.assessment_id]);

    // Auto-play on new question
    useEffect(() => {
        if (currentQuestion) {
            playAudioWithFallback(currentQuestion);
        }
    }, [currentQuestion]);


    const [isProcessing, setIsProcessing] = useState(false);

    const handleAnswer = async (value, callback) => {
        if (isProcessing || !currentQuestion) return;
        setIsProcessing(true);

        const userValue = value.trim();
        const isCorrect = currentQuestion.word.toLowerCase() === userValue.toLowerCase();

        // Prepare the object
        const submissionData = {
            ...currentQuestion,
            question: currentQuestion.sentence, // Forces the version with the $ for Laravel
            answer: userValue,                  // The student's typed response
            correct: isCorrect                  // Boolean for game logic
        };


        setQanswers(prev => [...prev, submissionData]);

        if (isCorrect) {
            setCorrectQuestions(prev => [...prev, submissionData]);
            setScore(prev => prev + 1);
        } else {
            setIncorrectQuestions(prev => [...prev, submissionData]);
        }

        // Pass the full object to the feedback callback
        if (callback) await callback(submissionData);

        const nextIdx = currentIndex + 1;
        if (nextIdx < filteredQuestions.length) {
            setCurrentIndex(nextIdx);
            setCurrentQuestion(filteredQuestions[nextIdx]);
        } else {
            setCurrentIndex(0);
            setCurrentQuestion(null);
        }
        setIsProcessing(false);
    };

    return (
        <NewSpellingGame
            {...props}
            loading={loading}
            correct={correctQuestions}
            incorrect={incorrectQuestions}
            qanswers={qanswers}
            score={score}
            questionLimit={filteredQuestions.length}
            feedback={true}
            canExit={true}
        >
            <Body>
                <Scale scaleTo={1720}>
                    <KeyboardWrapper>
                        {currentQuestion && (
                            <Keyboard
                                currentQuestion={currentQuestion}
                                singleUnderscore={true} // 👈 This maps straight to props.singleUnderscore
                                onSubmitted={handleAnswer}
                                playAudioButton={true}
                                onPlayAudio={() => playAudioWithFallback(currentQuestion)}
                                additionalCharacters={"abcdefghijklmnopqrstuvwxyz".split("")}
                            />
                        )}
                    </KeyboardWrapper>
                </Scale>
            </Body>
        </NewSpellingGame>
    );
}

const Body = styled.div`
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    padding-top: 180px;
    background: transparent; /* Background is handled by NewSpellingGame */
`;

const KeyboardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    /* Force controls to the bottom */
    [class*="controls"], .keyboard-controls, .side-bar {
        order: 2 !important;
        display: flex !important;
        flex-direction: row !important;
        width: 1200px !important; /* Fixed width to allow buttons to stretch */
        justify-content: center !important;
        gap: 50px !important;
        margin-top: 60px !important;
    }

    /* Target buttons aggressively to make them wider */
    button, [class*="button-submit"], [class*="button-audio"], .keyboard-button {
        flex: 1 !important; /* Allows them to fill the 1200px container */
        min-width: 500px !important; 
        height: 110px !important;
        font-size: 40px !important;
        border-radius: 20px !important;
        font-family: "PrimerPrint";
        box-shadow: 0 6px 0px #204b7b;
        cursor: pointer;
    }

    /* Key container on top */
    .keyboard-keys, [class*="keys-container"] {
        order: 1 !important;
    }
`;